/**
 * Objektübersicht (F1) — Liste + Karten über einer client-seitig gefilterten
 * `GET /property-groups`-Liste. Objekte sind niedrig-kardinal; Suche/Status/Sort/
 * Pagination laufen client-seitig über denselben (mit ObjectScope geteilten)
 * Cache und treiben beide Views identisch. Manage-Aktionen sind Admin/Staff-only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  EmptyState,
  PageHeader,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import { I } from '@/shared/icons';
import { useApi } from '@/shared/api';
import { canManage, useAuth } from '@/shared/auth';
import { filterAndSortObjects, type ObjectSort, type ObjectStatusFilter } from './objectOverview';
import { ObjectOverviewToolbar } from './ObjectOverviewToolbar';
import { ObjectCard } from './ObjectCard';
import { ObjectListRow } from './ObjectListRow';
import { useObjectView } from './useObjectView';

const PAGE_SIZE = 12;

export function PropertyGroupsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const canEdit = canManage(useAuth().user);
  const { view, storedView, setView, isMobile } = useObjectView();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ObjectStatusFilter>('all');
  const [sort, setSort] = useState<ObjectSort>('name-asc');
  const [page, setPage] = useState(1);

  const { data, error, isLoading } = useQuery({
    queryKey: ['property-groups'],
    queryFn: api.propertyGroups.list,
  });
  const groups = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(
    () => filterAndSortObjects(groups, { search, status, sort }),
    [groups, search, status, sort],
  );

  useEffect(() => setPage(1), [search, status, sort]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const reviewCount = groups.filter((group) => group.needsAddressReview).length;

  const createAction = canEdit && (
    <Button
      variant="brand"
      leftIcon={<I name="Plus" size={14} />}
      onClick={() => void navigate({ to: '/properties/new' })}
    >
      Neues Apartment
    </Button>
  );

  return (
    <>
      <PageHeader
        eyebrow="Standorte"
        title="Ihre"
        titleAccent="Objekte"
        description={
          data
            ? `${groups.length} Objekt${groups.length === 1 ? '' : 'e'}${
                reviewCount > 0 ? ` · ${reviewCount} in Prüfung` : ''
              }`
            : 'Adresse und gemeinsame Apartments werden an einem Ort geführt.'
        }
        actions={createAction}
      />

      {!error && groups.length > 0 && (
        <ObjectOverviewToolbar
          search={search}
          onSearch={setSearch}
          status={status}
          onStatus={setStatus}
          sort={sort}
          onSort={setSort}
          view={storedView}
          onView={setView}
          showViewToggle={!isMobile}
        />
      )}

      {error ? (
        <EmptyState
          icon="Error"
          title="Objekte konnten nicht geladen werden"
          description="Bitte Verbindung prüfen und erneut versuchen."
        />
      ) : !isLoading && groups.length === 0 ? (
        <EmptyState
          icon="Building"
          title="Noch keine Objekte"
          description="Beim ersten Apartment entsteht automatisch ein Objekt."
          {...(canEdit
            ? {
                action: {
                  label: 'Neues Apartment',
                  leftIcon: <I name="Plus" size={14} />,
                  onClick: () => void navigate({ to: '/properties/new' }),
                },
              }
            : {})}
        />
      ) : !isLoading && filtered.length === 0 ? (
        <EmptyState
          icon="Search"
          title="Keine Treffer"
          description="Suchbegriff oder Statusfilter anpassen."
          action={{
            label: 'Filter zurücksetzen',
            leftIcon: <I name="Refresh" size={14} />,
            onClick: () => {
              setSearch('');
              setStatus('all');
            },
          }}
        />
      ) : view === 'cards' ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {pageItems.map((group) => (
            <ObjectCard key={group.id} group={group} canManage={canEdit} />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Objekt</TableHead>
              <TableHead>Adresse</TableHead>
              <TableHead>Prüfstatus</TableHead>
              <TableHead>Apartments</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.map((group) => (
              <ObjectListRow key={group.id} group={group} canManage={canEdit} />
            ))}
          </TableBody>
        </Table>
      )}

      {!error && filtered.length > PAGE_SIZE && (
        <Pagination
          page={safePage}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onPageChange={setPage}
        />
      )}
    </>
  );
}
